import {
  addWireRecipientService,
  getWireRecipientRequestDetailsService,
  getWireRecipientListService,
  getWireRecipientRequestListService,
  getWireRecipientDetailsService,
  deleteWireRecipientRequestService,
  cancelWireRecipientRequestService,
} from '@/services/wireRecipients';
import { TransactionRequestSelectionItem } from '@/types/transactionRequests';
import {
  AddWireRecipientForm,
  queryKeys,
  WireRecipientListQueryParams,
  WireRecipientRequestListQueryParams,
} from '@/types/wireRecipients';
import { convertObjectValuesToString } from '@/utils/responseConvertors';
import { AccountBalance } from '@mui/icons-material';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import useIdempotencyKeyGenerator from '../useIdempotencyKeyGenerator';

function clearRecipientQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.recipientsList],
  });
  queryClient.invalidateQueries({
    queryKey: [queryKeys.recipientsDetails],
  });
}

function clearRequestQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.requestList],
  });
  queryClient.invalidateQueries({
    queryKey: [queryKeys.requestDetails],
  });
}

export const useGetWireRecipientsList = (
  queryParams: WireRecipientListQueryParams,
  enabled = true,
) => {
  const params = new URLSearchParams(
    convertObjectValuesToString(queryParams),
  ).toString();

  const { data, isPending, refetch } = useQuery({
    queryKey: [queryKeys.recipientsList, params],
    queryFn: () => getWireRecipientListService(params),
    enabled,
  });

  const wireRecipientsForSelection: TransactionRequestSelectionItem[] = data
    ? data.records.map(account => {
        const { uuid, accountName, bankCountryCode, bankLogoUrl } = account;

        return {
          value: uuid,
          label: accountName,
          isInternalAccount: false,
          bankAddressCountryCode: bankCountryCode,
          iconUrl: bankLogoUrl,
          icon: bankLogoUrl ? null : AccountBalance,
        };
      })
    : [];

  return {
    wireRecipients: data?.records || [],
    wireRecipientsForSelection,
    totalRecordCount: data?.totalRecordCount || 0,
    isWireRecipientsPending: isPending,
    refetchWireRecipients: refetch,
  };
};

export const useGetWireRecipientRequestsList = (
  queryParams: WireRecipientRequestListQueryParams,
) => {
  const params = new URLSearchParams(
    convertObjectValuesToString(queryParams),
  ).toString();

  const { data, isPending, refetch } = useQuery({
    queryKey: [queryKeys.requestList, params],
    queryFn: () => getWireRecipientRequestListService(params),
  });

  return {
    wireRecipientRequests: data?.records || [],
    totalRecordCount: data?.totalRecordCount || 0,
    isWireRecipientRequestsPending: isPending,
    refetchWireRecipients: refetch,
  };
};

export const useAddWireRecipient = () => {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: (data: AddWireRecipientForm) =>
      addWireRecipientService({ ...data, idempotencyKey }),
    onSuccess: () => {
      clearRecipientQueryCache(queryClient);
      clearRequestQueryCache(queryClient);
    },
  });

  return {
    addWireRecipient: mutateAsync,
    isAddWireRecipientPending: isPending,
  };
};

export const useGetWireRecipientRequestDetails = (uuid: string) => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.requestDetails, uuid],
    queryFn: () => getWireRecipientRequestDetailsService(uuid),
  });

  return {
    wireRecipientRequestDetails: data,
    isWireRecipientRequestDetailsPending: isPending,
  };
};

export const useGetWireRecipientDetails = (uuid: string) => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.recipientsDetails, uuid],
    queryFn: () => getWireRecipientDetailsService(uuid),
  });

  return {
    wireRecipientDetails: data,
    isWireRecipientDetailsPending: isPending,
  };
};

export const useCancelWireRecipientRequest = (uuid: string) => {
  const queryClient = useQueryClient();
  const { isPending, mutateAsync } = useMutation({
    mutationFn: () => cancelWireRecipientRequestService(uuid),
    onSuccess: () => {
      clearRequestQueryCache(queryClient);
    },
  });

  return {
    cancelWireRecipient: mutateAsync,
    isCancelWireRecipientPending: isPending,
  };
};

export const useDeleteWireRecipient = (uuid: string) => {
  const queryClient = useQueryClient();
  const { isPending, mutateAsync } = useMutation({
    mutationFn: () => deleteWireRecipientRequestService(uuid),
    onSuccess: () => {
      clearRecipientQueryCache(queryClient);
    },
  });

  return {
    deleteWireRecipient: mutateAsync,
    isDeleteWireRecipientPending: isPending,
  };
};
